import { useState, useEffect, useMemo } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import {
  subscribeToUserEvents, subscribeToUserJudgedBouts, subscribeToUserFighterBoutHistory,
  getEventsByIds, getUserProfile, updateUserProfile, deleteEvent,
  subscribeToPendingAdminRequests, respondToAdminRequest
} from "../firebase/services";
import { groupBoutsByEvent, groupBoutsByDiscipline, groupBoutsByFighterName } from "../utils/boutHistory";
import AdminAccessGate from "../components/AdminAccessGate";
import logoBadge from "../assets/logo-badge.png";
import logo from "../assets/logo.png";

const BLUE_LIGHT = "#64B5F6";
const RED_LIGHT = "#EF9A9A";

// Approving/denying admin requests is restricted to this one account for now
// (the user's request) rather than "any current admin" - see the matching
// check in api/approveAdminRequest.js, which is the actual enforcement point;
// this just hides the tab from other admins who'd get a 403 from the API anyway.
const SUPER_ADMIN_EMAIL = "ckimbell@gmail.com";

export default function Account() {
  const { user, logout, isAdmin } = useAuth();
  const canApproveAdmins = isAdmin && user?.email?.toLowerCase() === SUPER_ADMIN_EMAIL;
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get("tab") || "events";
  const setActiveTab = (tab) => setSearchParams((prev) => {
    const next = new URLSearchParams(prev);
    next.set("tab", tab);
    return next;
  });
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deletingEventId, setDeletingEventId] = useState(null);

  useEffect(() => {
    if (!user) return;
    const unsub = subscribeToUserEvents(user.uid, (evts) => {
      setEvents(evts);
      setLoading(false);
    });
    return unsub;
  }, [user]);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const handleDeleteEvent = async (evt) => {
    if (!window.confirm(`Permanently delete "${evt.name}"? This also deletes all of its disciplines, bouts, and scores. This can't be undone.`)) return;
    setDeletingEventId(evt.id);
    try {
      await deleteEvent(evt.id);
    } catch (err) {
      console.error(err);
      alert("Failed to delete event");
    }
    setDeletingEventId(null);
  };

  if (!user) return null;

  const activeEvents = events.filter(e => e.status === "active");
  const completedEvents = events.filter(e => e.status !== "active");

  return (
    <div className="min-h-screen p-5 lg:max-w-5xl lg:mx-auto">
      <Link to="/" className="text-gray-600 text-sm no-underline hover:text-gray-400">← Home</Link>

      <div className="text-center my-4">
        <img src={logo} alt="Joy of HEMA" className="w-80 mx-auto" />
        <div className="flex items-center gap-3 max-w-[280px] mx-auto mt-2">
          <div className="flex-1 h-px bg-dark-500" />
          <span className="text-gray-400 text-sm font-display font-bold tracking-[0.25em] whitespace-nowrap">
            STUDY &bull; FENCE &bull; GROW
          </span>
          <div className="flex-1 h-px bg-dark-500" />
        </div>
      </div>

      {/* User Info */}
      <div className="mt-4 mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-display text-xl font-bold">{user.displayName || "My Account"}</h1>
            <p className="text-gray-500 text-sm mt-0.5">{user.email}</p>
          </div>
          <button onClick={handleLogout}
            className="px-3 py-1.5 bg-dark-700 border border-dark-500 rounded-lg text-gray-500 text-xs cursor-pointer hover:text-gray-300 hover:border-gray-600">
            Sign out
          </button>
        </div>
      </div>

      {/* Tab bar */}
      <div className="flex gap-1 mb-5 border-b border-dark-500">
        {[
          { key: "events", label: "YOUR EVENTS" },
          { key: "judging", label: "JUDGING HISTORY" },
          { key: "mybouts", label: "MY BOUTS" },
          { key: "profile", label: "PROFILE" },
          ...(canApproveAdmins ? [{ key: "adminRequests", label: "ADMIN REQUESTS" }] : []),
        ].map(t => (
          <button key={t.key} onClick={() => setActiveTab(t.key)} aria-pressed={activeTab === t.key}
            className="px-4 py-2.5 font-display text-xs font-bold tracking-widest whitespace-nowrap border-b-2 bg-transparent cursor-pointer"
            style={{ color: activeTab === t.key ? "#fff" : "#bbb", borderColor: activeTab === t.key ? "#81C784" : "transparent" }}>
            {t.label}
          </button>
        ))}
      </div>

      {activeTab === "events" && (
      <>
      {/* Your Events */}
      <div className="mb-6">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="flex gap-1">
              {[0, 1, 2].map(i => (
                <div key={i} className="w-1.5 h-1.5 rounded-full bg-gray-600 pulse-dot" style={{ animationDelay: `${i * 0.2}s` }} />
              ))}
            </div>
          </div>
        ) : events.length === 0 ? (
          <div className="bg-dark-700 border border-dark-500 rounded-xl p-6 text-center">
            <img src={logoBadge} alt="" className="w-14 h-14 mx-auto mb-3" />
            <p className="text-gray-500 text-sm mb-1">No events yet</p>
            <p className="text-gray-600 text-xs">Create your first event below</p>
          </div>
        ) : (
          <>
            {activeEvents.length > 0 && (
              <div className="mb-4">
                {activeEvents
                  .sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0))
                  .map(evt => (
                    <EventCard key={evt.id} event={evt} navigate={navigate}
                      onDelete={handleDeleteEvent} deleting={deletingEventId === evt.id} />
                  ))}
              </div>
            )}
            {completedEvents.length > 0 && (
              <div>
                <div className="text-xs font-bold text-gray-600 tracking-widest mb-2">COMPLETED</div>
                {completedEvents
                  .sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0))
                  .map(evt => (
                    <EventCard key={evt.id} event={evt} navigate={navigate}
                      onDelete={handleDeleteEvent} deleting={deletingEventId === evt.id} />
                  ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* Create New Event */}
      {isAdmin ? (
        <Link to="/create"
          className="block w-full p-4 bg-gradient-to-br from-green-action-light to-green-action rounded-xl text-center no-underline hover:opacity-90 transition-opacity">
          <div className="font-display text-base font-bold text-white tracking-wide">+ Create New Event</div>
        </Link>
      ) : (
        <AdminAccessGate />
      )}
      </>
      )}

      {activeTab === "judging" && <JudgingHistory user={user} />}

      {activeTab === "mybouts" && <FighterBoutHistory user={user} />}

      {activeTab === "profile" && <ProfileTab user={user} />}

      {activeTab === "adminRequests" && canApproveAdmins && <AdminRequestsPanel />}
    </div>
  );
}

function AdminRequestsPanel() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [respondingTo, setRespondingTo] = useState(null);

  useEffect(() => {
    const unsub = subscribeToPendingAdminRequests((reqs) => {
      setRequests(reqs);
      setLoading(false);
    });
    return unsub;
  }, []);

  const handleRespond = async (targetUid, action) => {
    setRespondingTo(targetUid);
    try {
      await respondToAdminRequest(targetUid, action);
    } catch (err) {
      console.error(err);
      alert("Failed to respond to request");
    }
    setRespondingTo(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="flex gap-1">
          {[0, 1, 2].map(i => (
            <div key={i} className="w-1.5 h-1.5 rounded-full bg-gray-600 pulse-dot" style={{ animationDelay: `${i * 0.2}s` }} />
          ))}
        </div>
      </div>
    );
  }

  if (requests.length === 0) {
    return (
      <div className="bg-dark-700 border border-dark-500 rounded-xl p-6 text-center">
        <div className="text-3xl mb-3">✓</div>
        <p className="text-gray-500 text-sm">No pending admin requests</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      {requests.map(r => (
        <div key={r.uid} className="bg-dark-700 border border-dark-500 rounded-xl p-4">
          <div className="font-display text-sm font-bold text-white">{r.name || "(no name)"}</div>
          <div className="text-gray-500 text-xs mb-2">{r.email}{r.affiliation ? ` • ${r.affiliation}` : ""}</div>
          {r.reason && (
            <div className="bg-dark-800 rounded-lg p-2.5 text-gray-400 text-xs mb-3">{r.reason}</div>
          )}
          <div className="flex gap-2">
            <button onClick={() => handleRespond(r.uid, "approve")} disabled={respondingTo === r.uid}
              className="flex-1 py-2 bg-gradient-to-br from-green-action-light to-green-action rounded-lg text-white text-xs font-bold tracking-wide disabled:opacity-40 border-none cursor-pointer">
              Approve
            </button>
            <button onClick={() => handleRespond(r.uid, "deny")} disabled={respondingTo === r.uid}
              className="flex-1 py-2 bg-dark-800 border border-dark-500 rounded-lg text-gray-400 text-xs font-bold tracking-wide disabled:opacity-40 cursor-pointer hover:text-white">
              Deny
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

function ProfileTab({ user }) {
  const { updateDisplayName } = useAuth();
  const [name, setName] = useState(user.displayName || "");
  const [affiliation, setAffiliation] = useState("");
  const [bio, setBio] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    getUserProfile(user.uid).then(profile => {
      setAffiliation(profile.affiliation || "");
      setBio(profile.bio || "");
      setLoading(false);
    });
  }, [user.uid]);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setSaved(false);
    const trimmedName = name.trim();
    if (trimmedName && trimmedName !== user.displayName) {
      await updateDisplayName(trimmedName);
    }
    await updateUserProfile(user.uid, { affiliation, bio });
    setSaving(false);
    setSaved(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="flex gap-1">
          {[0, 1, 2].map(i => (
            <div key={i} className="w-1.5 h-1.5 rounded-full bg-gray-600 pulse-dot" style={{ animationDelay: `${i * 0.2}s` }} />
          ))}
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSave} className="flex flex-col gap-4">
      <div>
          <label htmlFor="account-name" className="font-display text-xs font-bold text-gray-500 tracking-widest block mb-1.5">NAME</label>
          <input id="account-name" value={name} onChange={(e) => { setName(e.target.value); setSaved(false); }}
          placeholder="Your name"
          className="w-full p-3 bg-dark-800 border border-dark-500 rounded-lg text-white text-sm outline-none focus:border-gray-400" />
      </div>
      <div>
          <label htmlFor="account-affiliation" className="font-display text-xs font-bold text-gray-500 tracking-widest block mb-1.5">AFFILIATION</label>
          <input id="account-affiliation" value={affiliation} onChange={(e) => { setAffiliation(e.target.value); setSaved(false); }}
          placeholder="School / club"
          className="w-full p-3 bg-dark-800 border border-dark-500 rounded-lg text-white text-sm outline-none focus:border-gray-400" />
      </div>
      <div>
          <label htmlFor="account-bio" className="font-display text-xs font-bold text-gray-500 tracking-widest block mb-1.5">BIO</label>
          <textarea id="account-bio" value={bio} onChange={(e) => { setBio(e.target.value); setSaved(false); }}
          placeholder="A little about you..." rows={4}
          className="w-full p-3 bg-dark-800 border border-dark-500 rounded-lg text-white text-sm outline-none focus:border-gray-400 resize-none" />
      </div>
      <button type="submit" disabled={saving}
        className="w-full p-3 bg-gradient-to-br from-green-action-light to-green-action text-white font-display text-sm font-bold rounded-xl tracking-wide disabled:opacity-40 border-none cursor-pointer">
        {saving ? "Saving..." : saved ? "Saved ✓" : "Save Profile"}
      </button>
    </form>
  );
}

function EventCard({ event, navigate, onDelete, deleting }) {
  const typeLabels = { tournament: "Tournament", seminar: "Judging Seminar", training: "Training" };
  const createdDate = event.createdAt?.seconds
    ? new Date(event.createdAt.seconds * 1000).toLocaleDateString()
    : "";

  return (
    <div className="relative mb-2">
      <button onClick={() => navigate(`/event/${event.id}`)} disabled={deleting}
        className="w-full text-left bg-dark-700 border border-dark-500 rounded-xl p-4 pr-10 cursor-pointer hover:border-gray-600 transition-colors disabled:opacity-40">
        <div className="font-display text-base font-bold text-white mb-1">{event.name}</div>
        <div className="flex items-center gap-3 text-xs text-gray-600">
          <span>{typeLabels[event.type] || event.type}</span>
          <span>Code: <span className="font-display font-bold text-gray-400 tracking-wider">{event.eventCode}</span></span>
          {createdDate && <span>{createdDate}</span>}
        </div>
        {event.type === "tournament" && event.disciplines?.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-2">
            {event.disciplines.map(d => (
              <span key={d} className="px-2 py-0.5 bg-dark-800 border border-dark-500 rounded text-[10px] text-gray-500">{d}</span>
            ))}
          </div>
        )}
      </button>
      <button onClick={(e) => { e.stopPropagation(); onDelete(event); }} disabled={deleting}
        aria-label={`Delete ${event.name} event`}
        title="Delete event"
        className="absolute top-4 right-4 min-w-6 min-h-6 text-gray-600 hover:text-red-400 bg-transparent border-none cursor-pointer text-sm disabled:opacity-40">
        {deleting ? "…" : "✕"}
      </button>
    </div>
  );
}

// Finds this user's own scorecard within a bout's judgeSubmissions map — a bout can have
// several judges, only one of whom (at most) is this logged-in user.
const mySubmission = (bout) => {
  const judgeNames = bout._history?.judgeNames || [];
  return Object.values(bout.judgeSubmissions || {})
    .find((submission) => judgeNames.includes(submission.judgeName)) || null;
};

const boutPhaseLabel = (b) =>
  b.phase === "elimination"
    ? `Elimination • Round ${(b.eliminationRound ?? 0) + 1}`
    : b.poolName || "Pool";

function JudgingHistory({ user }) {
  const [bouts, setBouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [eventsById, setEventsById] = useState({});
  const [selectedEventId, setSelectedEventId] = useState(null);
  const [selectedDisciplineId, setSelectedDisciplineId] = useState(null);

  useEffect(() => {
    if (!user) return;
    const unsub = subscribeToUserJudgedBouts(user.uid, (b) => {
      setBouts(b);
      setLoading(false);
    });
    return unsub;
  }, [user]);

  useEffect(() => {
    const ids = [...new Set(bouts.map(b => b.eventId))].filter(id => !eventsById[id]);
    if (ids.length === 0) return;
    getEventsByIds(ids).then(fetched => {
      setEventsById(prev => {
        const next = { ...prev };
        fetched.forEach(e => { next[e.id] = e; });
        return next;
      });
    });
  }, [bouts]);

  // Keep only bouts where this user actually recorded a scorecard.
  const judgedBouts = useMemo(
    () => bouts.filter(b => mySubmission(b)?.rounds?.length > 0),
    [bouts]
  );

  const eventGroups = useMemo(() => groupBoutsByEvent(judgedBouts, eventsById), [judgedBouts, eventsById]);

  const selectedGroup = eventGroups.find(g => g.eventId === selectedEventId) || null;

  const disciplineGroups = useMemo(
    () => selectedGroup ? groupBoutsByDiscipline(selectedGroup.bouts) : [],
    [selectedGroup]
  );

  const noDisciplineLevel = disciplineGroups.length === 1 && disciplineGroups[0].disciplineId === "__none__";
  const effectiveDisciplineId = selectedDisciplineId || (noDisciplineLevel ? disciplineGroups[0].disciplineId : null);
  const selectedDiscipline = disciplineGroups.find(d => d.disciplineId === effectiveDisciplineId) || null;

  const backToEvents = () => { setSelectedEventId(null); setSelectedDisciplineId(null); };
  const backToDisciplines = () => setSelectedDisciplineId(null);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="flex gap-1">
          {[0, 1, 2].map(i => (
            <div key={i} className="w-1.5 h-1.5 rounded-full bg-gray-600 pulse-dot" style={{ animationDelay: `${i * 0.2}s` }} />
          ))}
        </div>
      </div>
    );
  }

  if (judgedBouts.length === 0) {
    return (
      <div className="bg-dark-700 border border-dark-500 rounded-xl p-6 text-center">
        <p className="text-gray-500 text-sm mb-1">No judging history yet</p>
        <p className="text-gray-600 text-xs">Bouts you score while logged in will show up here</p>
      </div>
    );
  }

  // ─── LEVEL 3: bouts within a discipline (or a whole non-tournament event) ───
  if (selectedDiscipline) {
    const sortedBouts = [...selectedDiscipline.bouts].sort((a, b) => (a.createdAt?.seconds || 0) - (b.createdAt?.seconds || 0));
    return (
      <div>
        <button onClick={noDisciplineLevel ? backToEvents : backToDisciplines}
          className="text-gray-600 text-xs mb-3 bg-transparent border-none cursor-pointer hover:text-gray-400 p-0">
          ← {noDisciplineLevel ? "All Events" : selectedGroup.event?.name || "Disciplines"}
        </button>
        <div className="font-display text-xs font-bold text-gray-500 tracking-widest mb-3">
          {selectedGroup.event?.name}{!noDisciplineLevel && ` • ${selectedDiscipline.disciplineName}`} ({sortedBouts.length})
        </div>
        <div className="flex flex-col gap-2">
          {sortedBouts.map(b => <JudgedBoutCard key={b.id} bout={b} />)}
        </div>
      </div>
    );
  }

  // ─── LEVEL 2: disciplines within an event ───
  if (selectedGroup) {
    return (
      <div>
        <button onClick={backToEvents}
          className="text-gray-600 text-xs mb-3 bg-transparent border-none cursor-pointer hover:text-gray-400 p-0">
          ← All Events
        </button>
        <div className="font-display text-xs font-bold text-gray-500 tracking-widest mb-3">
          {selectedGroup.event?.name || "Event"}
        </div>
        <div className="flex flex-col gap-2">
          {disciplineGroups.map(d => (
            <button key={d.disciplineId} onClick={() => setSelectedDisciplineId(d.disciplineId)}
              className="w-full text-left bg-dark-700 border border-dark-500 rounded-xl p-4 cursor-pointer hover:border-gray-600 transition-colors">
              <div className="flex items-center justify-between">
                <div className="font-display text-base font-bold text-white">{d.disciplineName}</div>
                <div className="flex items-center gap-2">
                  <span className="text-gray-600 text-xs">{d.bouts.length} bout{d.bouts.length === 1 ? "" : "s"}</span>
                  <span className="text-gray-700 text-lg">→</span>
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>
    );
  }

  // ─── LEVEL 1: events ───
  return (
    <div className="flex flex-col gap-2">
      {eventGroups.map(g => (
        <button key={g.eventId} onClick={() => setSelectedEventId(g.eventId)}
          className="w-full text-left bg-dark-700 border border-dark-500 rounded-xl p-4 cursor-pointer hover:border-gray-600 transition-colors">
          <div className="flex items-center justify-between">
            <div className="font-display text-base font-bold text-white">{g.event?.name || "Event"}</div>
            <div className="flex items-center gap-2">
              <span className="text-gray-600 text-xs">{g.bouts.length} bout{g.bouts.length === 1 ? "" : "s"}</span>
              <span className="text-gray-700 text-lg">→</span>
            </div>
          </div>
        </button>
      ))}
    </div>
  );
}

function JudgedBoutCard({ bout }) {
  const sub = mySubmission(bout);
  const myTotalBlue = sub?.rounds?.reduce((s, r) => s + (r.blue || 0), 0) || 0;
  const myTotalRed = sub?.rounds?.reduce((s, r) => s + (r.red || 0), 0) || 0;
  const isConfirmed = bout.status === "confirmed" && bout.confirmedResult;

  return (
    <div className="bg-dark-700 border border-dark-500 rounded-xl p-4">
      <div className="flex items-center justify-between mb-2">
        <span className="text-gray-600 text-[11px] font-semibold tracking-wide">{boutPhaseLabel(bout)}</span>
        <span className="text-[10px] font-bold tracking-wide px-2 py-0.5 rounded-full"
          style={{ color: isConfirmed ? "#4CAF50" : "#FF9800", background: isConfirmed ? "#4CAF5022" : "#FF980022" }}>
          {isConfirmed ? "CONFIRMED" : sub?.complete ? "SUBMITTED" : "IN PROGRESS"}
        </span>
      </div>
      <div className="flex items-center justify-center gap-4 mb-2">
        <span className="font-display text-sm font-bold" style={{ color: BLUE_LIGHT }}>{bout.fighterBlue}</span>
        <span className="text-gray-700 text-xs">vs</span>
        <span className="font-display text-sm font-bold" style={{ color: RED_LIGHT }}>{bout.fighterRed}</span>
      </div>
      <div className="grid grid-cols-2 gap-3 text-center">
        <div className="bg-dark-800 rounded-lg p-2">
          <div className="text-[9px] font-bold text-gray-600 tracking-widest mb-1">YOUR CARD</div>
          <div className="font-display text-lg font-extrabold">
            <span style={{ color: BLUE_LIGHT }}>{myTotalBlue}</span>
            <span className="text-gray-700 mx-1">–</span>
            <span style={{ color: RED_LIGHT }}>{myTotalRed}</span>
          </div>
        </div>
        <div className="bg-dark-800 rounded-lg p-2">
          <div className="text-[9px] font-bold text-gray-600 tracking-widest mb-1">FINAL RESULT</div>
          {isConfirmed ? (
            <div className="font-display text-lg font-extrabold">
              <span style={{ color: BLUE_LIGHT }}>{bout.confirmedResult.scoreB}</span>
              <span className="text-gray-700 mx-1">–</span>
              <span style={{ color: RED_LIGHT }}>{bout.confirmedResult.scoreA}</span>
            </div>
          ) : (
            <div className="text-xs text-gray-600 mt-1.5">Pending</div>
          )}
        </div>
      </div>
    </div>
  );
}

function FighterBoutHistory({ user }) {
  const [bouts, setBouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [eventsById, setEventsById] = useState({});
  const [selectedEventId, setSelectedEventId] = useState(null);
  const [selectedDisciplineId, setSelectedDisciplineId] = useState(null);
  const [selectedFighterName, setSelectedFighterName] = useState(null);

  useEffect(() => {
    if (!user) return;
    const unsub = subscribeToUserFighterBoutHistory(user.uid, (b) => {
      setBouts(b);
      setLoading(false);
    });
    return unsub;
  }, [user]);

  useEffect(() => {
    const ids = [...new Set(bouts.map(b => b.eventId))].filter(id => !eventsById[id]);
    if (ids.length === 0) return;
    getEventsByIds(ids).then(fetched => {
      setEventsById(prev => {
        const next = { ...prev };
        fetched.forEach(e => { next[e.id] = e; });
        return next;
      });
    });
  }, [bouts]);

  const eventGroups = useMemo(() => groupBoutsByEvent(bouts, eventsById), [bouts, eventsById]);

  const selectedGroup = eventGroups.find(g => g.eventId === selectedEventId) || null;

  const disciplineGroups = useMemo(
    () => selectedGroup ? groupBoutsByDiscipline(selectedGroup.bouts) : [],
    [selectedGroup]
  );

  const noDisciplineLevel = disciplineGroups.length === 1 && disciplineGroups[0].disciplineId === "__none__";
  const effectiveDisciplineId = selectedDisciplineId || (noDisciplineLevel ? disciplineGroups[0].disciplineId : null);
  const selectedDiscipline = disciplineGroups.find(d => d.disciplineId === effectiveDisciplineId) || null;

  // One account can be linked to more than one fighter name (e.g. a parent's two kids) —
  // split a discipline's bouts by whichever name matched this uid so they aren't
  // interleaved under one list. Skipped entirely (like noDisciplineLevel) when the
  // account only maps to a single name here, the overwhelmingly common case.
  const fighterNameGroups = useMemo(
    () => selectedDiscipline ? groupBoutsByFighterName(selectedDiscipline.bouts) : [],
    [selectedDiscipline]
  );
  const noNameLevel = fighterNameGroups.length <= 1;
  const effectiveFighterName = selectedFighterName || (noNameLevel ? fighterNameGroups[0]?.name : null);
  const selectedNameGroup = fighterNameGroups.find(g => g.name === effectiveFighterName) || null;

  const backToEvents = () => { setSelectedEventId(null); setSelectedDisciplineId(null); setSelectedFighterName(null); };
  const backToDisciplines = () => { setSelectedDisciplineId(null); setSelectedFighterName(null); };
  const backToNames = () => setSelectedFighterName(null);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="flex gap-1">
          {[0, 1, 2].map(i => (
            <div key={i} className="w-1.5 h-1.5 rounded-full bg-gray-600 pulse-dot" style={{ animationDelay: `${i * 0.2}s` }} />
          ))}
        </div>
      </div>
    );
  }

  if (bouts.length === 0) {
    return (
      <div className="bg-dark-700 border border-dark-500 rounded-xl p-6 text-center">
        <p className="text-gray-500 text-sm mb-1">No bout history yet</p>
        <p className="text-gray-600 text-xs">Once an event admin links your account to a fighter roster, your bouts show up here</p>
      </div>
    );
  }

  // ─── LEVEL 4: bouts for one fighter name within a discipline ───
  if (selectedDiscipline && selectedNameGroup) {
    const sortedBouts = [...selectedNameGroup.bouts].sort((a, b) => (a.createdAt?.seconds || 0) - (b.createdAt?.seconds || 0));
    return (
      <div>
        <button onClick={noNameLevel ? (noDisciplineLevel ? backToEvents : backToDisciplines) : backToNames}
          className="text-gray-600 text-xs mb-3 bg-transparent border-none cursor-pointer hover:text-gray-400 p-0">
          ← {noNameLevel ? (noDisciplineLevel ? "All Events" : selectedGroup.event?.name || "Disciplines") : "Fighters"}
        </button>
        <div className="font-display text-xs font-bold text-gray-500 tracking-widest mb-3">
          {selectedGroup.event?.name}{!noDisciplineLevel && ` • ${selectedDiscipline.disciplineName}`}
          {!noNameLevel && ` • ${selectedNameGroup.name}`} ({sortedBouts.length})
        </div>
        <div className="flex flex-col gap-2">
          {sortedBouts.map(b => <FighterBoutCard key={b.id} bout={b} />)}
        </div>
      </div>
    );
  }

  // ─── LEVEL 3: choose which fighter name within a discipline (only when this
  // account is linked to more than one name in it — see fighterNameGroups above) ───
  if (selectedDiscipline) {
    return (
      <div>
        <button onClick={noDisciplineLevel ? backToEvents : backToDisciplines}
          className="text-gray-600 text-xs mb-3 bg-transparent border-none cursor-pointer hover:text-gray-400 p-0">
          ← {noDisciplineLevel ? "All Events" : selectedGroup.event?.name || "Disciplines"}
        </button>
        <div className="font-display text-xs font-bold text-gray-500 tracking-widest mb-3">
          {selectedGroup.event?.name}{!noDisciplineLevel && ` • ${selectedDiscipline.disciplineName}`}
        </div>
        <div className="flex flex-col gap-2">
          {fighterNameGroups.map(g => (
            <button key={g.name} onClick={() => setSelectedFighterName(g.name)}
              className="w-full text-left bg-dark-700 border border-dark-500 rounded-xl p-4 cursor-pointer hover:border-gray-600 transition-colors">
              <div className="flex items-center justify-between">
                <div className="font-display text-base font-bold text-white">{g.name}</div>
                <div className="flex items-center gap-2">
                  <span className="text-gray-600 text-xs">{g.bouts.length} bout{g.bouts.length === 1 ? "" : "s"}</span>
                  <span className="text-gray-700 text-lg">→</span>
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>
    );
  }

  // ─── LEVEL 2: disciplines within an event ───
  if (selectedGroup) {
    return (
      <div>
        <button onClick={backToEvents}
          className="text-gray-600 text-xs mb-3 bg-transparent border-none cursor-pointer hover:text-gray-400 p-0">
          ← All Events
        </button>
        <div className="font-display text-xs font-bold text-gray-500 tracking-widest mb-3">
          {selectedGroup.event?.name || "Event"}
        </div>
        <div className="flex flex-col gap-2">
          {disciplineGroups.map(d => (
            <button key={d.disciplineId} onClick={() => setSelectedDisciplineId(d.disciplineId)}
              className="w-full text-left bg-dark-700 border border-dark-500 rounded-xl p-4 cursor-pointer hover:border-gray-600 transition-colors">
              <div className="flex items-center justify-between">
                <div className="font-display text-base font-bold text-white">{d.disciplineName}</div>
                <div className="flex items-center gap-2">
                  <span className="text-gray-600 text-xs">{d.bouts.length} bout{d.bouts.length === 1 ? "" : "s"}</span>
                  <span className="text-gray-700 text-lg">→</span>
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>
    );
  }

  // ─── LEVEL 1: events ───
  return (
    <div className="flex flex-col gap-2">
      {eventGroups.map(g => (
        <button key={g.eventId} onClick={() => setSelectedEventId(g.eventId)}
          className="w-full text-left bg-dark-700 border border-dark-500 rounded-xl p-4 cursor-pointer hover:border-gray-600 transition-colors">
          <div className="flex items-center justify-between">
            <div className="font-display text-base font-bold text-white">{g.event?.name || "Event"}</div>
            <div className="flex items-center gap-2">
              <span className="text-gray-600 text-xs">{g.bouts.length} bout{g.bouts.length === 1 ? "" : "s"}</span>
              <span className="text-gray-700 text-lg">→</span>
            </div>
          </div>
        </button>
      ))}
    </div>
  );
}

const OUTCOME_STYLE = {
  win: { label: "WIN", color: "#4CAF50", bg: "#4CAF5022" },
  loss: { label: "LOSS", color: "#E53935", bg: "#E5393522" },
  tie: { label: "TIE", color: "#FFB300", bg: "#FFB30022" },
};

function FighterBoutCard({ bout }) {
  const fighterNames = bout._history?.fighterNames || [];
  const isBlue = fighterNames.includes(bout.fighterBlue);
  const myName = isBlue ? bout.fighterBlue : bout.fighterRed;
  const oppName = isBlue ? bout.fighterRed : bout.fighterBlue;
  const isConfirmed = bout.status === "confirmed" && bout.confirmedResult;
  const myScore = isConfirmed ? (isBlue ? bout.confirmedResult.scoreB : bout.confirmedResult.scoreA) : null;
  const oppScore = isConfirmed ? (isBlue ? bout.confirmedResult.scoreA : bout.confirmedResult.scoreB) : null;
  const outcome = !isConfirmed ? null
    : bout.confirmedResult.winner === myName ? "win"
    : bout.confirmedResult.winner && bout.confirmedResult.winner !== "Draw" ? "loss" : "tie";
  const outcomeStyle = outcome ? OUTCOME_STYLE[outcome] : null;

  return (
    <div className="bg-dark-700 border border-dark-500 rounded-xl p-4">
      <div className="flex items-center justify-between mb-2">
        <span className="text-gray-600 text-[11px] font-semibold tracking-wide">{boutPhaseLabel(bout)}</span>
        {outcomeStyle ? (
          <span className="text-[10px] font-bold tracking-wide px-2 py-0.5 rounded-full"
            style={{ color: outcomeStyle.color, background: outcomeStyle.bg }}>
            {outcomeStyle.label}
          </span>
        ) : (
          <span className="text-[10px] font-bold tracking-wide px-2 py-0.5 rounded-full text-gray-500" style={{ background: "#88888822" }}>
            IN PROGRESS
          </span>
        )}
      </div>
      <div className="flex items-center justify-center gap-4 mb-2">
        <span className="font-display text-sm font-bold" style={{ color: isBlue ? BLUE_LIGHT : RED_LIGHT }}>{myName} (you)</span>
        <span className="text-gray-700 text-xs">vs</span>
        <span className="font-display text-sm font-bold" style={{ color: isBlue ? RED_LIGHT : BLUE_LIGHT }}>{oppName}</span>
      </div>
      <div className="bg-dark-800 rounded-lg p-2 text-center">
        <div className="text-[9px] font-bold text-gray-600 tracking-widest mb-1">RESULT</div>
        {isConfirmed ? (
          <div className="font-display text-lg font-extrabold">
            <span style={{ color: isBlue ? BLUE_LIGHT : RED_LIGHT }}>{myScore}</span>
            <span className="text-gray-700 mx-1">–</span>
            <span style={{ color: isBlue ? RED_LIGHT : BLUE_LIGHT }}>{oppScore}</span>
          </div>
        ) : (
          <div className="text-xs text-gray-600 mt-1.5">Pending</div>
        )}
      </div>
    </div>
  );
}
